import { createContext, FC, ReactNode, useContext } from 'react'

/**
 * Entity context for the Power Analytics event stream. The document viewer fires
 * its telemetry (`DOC_SEARCH_QUERY/SELECT/RESULTS/DWELL`) knowing only the
 * `site_id`; when an embedder (e.g. the Dossiers page) wraps the viewer in a
 * {@link PowerAnalyticsEntityProvider}, `usePowerAnalyticsBackend` merges these
 * fields into every event payload so the stream is attributable to the dossier /
 * asset the user is investigating. Every field is optional: with no provider the
 * standalone Documents page sends byte-identical payloads as before.
 */
export interface PowerAnalyticsEntityContext {
  /** The dossier (`fim-claim-…`) being investigated, on the datastreams tab. */
  dossier_id?: string
  /** The Asset Instance (`fim-asset-instance-…`) being investigated, on the
   * assets tab. */
  asset_id?: string
  /** The dossier's tag (datastreams only). */
  pi_tag?: string
  /** Which Dossiers tab the viewer is embedded in. */
  view?: 'datastreams' | 'assets'
  organization_id?: string
}

const PowerAnalyticsContext = createContext<
  PowerAnalyticsEntityContext | undefined
>(undefined)

export const PowerAnalyticsEntityProvider: FC<{
  value: PowerAnalyticsEntityContext
  children: ReactNode
}> = ({ value, children }) => (
  <PowerAnalyticsContext.Provider value={value}>
    {children}
  </PowerAnalyticsContext.Provider>
)

export const usePowerAnalyticsEntityContext = ():
  | PowerAnalyticsEntityContext
  | undefined => useContext(PowerAnalyticsContext)
